/**
 * Toolbar flutuante que aparece ao selecionar um node no Flow Builder
 * Ações: Visualizar/Editar, Testar daqui, Copiar ID, Duplicar, Excluir
 * Para nodes tipo 'group': Desagrupar, Excluir grupo
 * 
 * RELEASE: 1.1.0 - 2026-03-07 - Adicionado botão Play (Testar a partir deste node)
 * RELEASE: 1.2.0 - 2026-03-07 - Adicionado suporte a nodes do tipo 'group' (Desagrupar)
 */

import { NodeToolbar, Position } from '@xyflow/react';
import { Eye, Info, Copy, Trash2, Play, Minimize2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { showToast } from '@/lib/toast';

interface FlowNodeToolbarProps {
  /** ID único do node */
  nodeId: string;
  /** Tipo do node (start, end, message_text, group, etc) */
  nodeType: string;
  /** Callback ao clicar em visualizar/editar */
  onEdit?: () => void;
  /** Callback ao clicar em duplicar */
  onDuplicate?: () => void;
  /** Callback ao clicar em excluir */
  onDelete?: () => void;
  /** Callback ao clicar em testar a partir deste node */
  onTestFromHere?: () => void;
  /** Callback ao clicar em desagrupar (apenas para tipo 'group') */
  onUngroup?: () => void;
  /** Callback ao entrar no modo de edição de grupo (adicionar nodes) */
  onEnterEditMode?: () => void;
  /** Callback ao duplicar grupo completo com filhos e edges */
  onDuplicateGroup?: () => void;
  /** Se a toolbar está visível (node selecionado) */
  isVisible?: boolean;
}

/**
 * Toolbar flutuante com ações para nodes do Flow Builder
 * Inspirado no design do Typebot
 */
export function FlowNodeToolbar({
  nodeId,
  nodeType,
  onEdit,
  onDuplicate,
  onDelete,
  onTestFromHere,
  onUngroup,
  onEnterEditMode,
  onDuplicateGroup,
  isVisible = true,
}: FlowNodeToolbarProps) {
  // Nodes protegidos que não podem ser editados/excluídos/duplicados
  const isProtectedNode = nodeType === 'start' || nodeType === 'end';
  // Nodes do tipo grupo têm toolbar específica
  const isGroupNode = nodeType === 'group';

  const canEdit = !isProtectedNode && !isGroupNode && !!onEdit;
  const canDuplicate = !isProtectedNode && !isGroupNode && !!onDuplicate;
  const canDelete = !isProtectedNode && !!onDelete;
  // Botão Play: disponível para qualquer node não-protegido e não-grupo
  const canTestFromHere = !isProtectedNode && !isGroupNode && !!onTestFromHere;
  // Botão Desagrupar: apenas para nodes de grupo
  const canUngroup = isGroupNode && !!onUngroup;
  // Botão Adicionar nodes: apenas para nodes de grupo
  const canEnterEditMode = isGroupNode && !!onEnterEditMode;
  // Botão Duplicar grupo: apenas para nodes de grupo
  const canDuplicateGroup = isGroupNode && !!onDuplicateGroup;

  /**
   * Copia o ID do node para a área de transferência
   */
  const handleCopyId = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(nodeId);
      showToast.success('ID copiado!', `Node ID: ${nodeId.substring(0, 20)}...`);
    } catch (error) {
      showToast.error('Erro ao copiar', 'Não foi possível copiar o ID');
    }
  };

  /**
   * Executa a ação de duplicar o node
   */
  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isProtectedNode) {
      showToast.warning('Ação bloqueada', 'Nodes de início e fim não podem ser duplicados');
      return;
    }
    onDuplicate?.();
  };

  /**
   * Executa a ação de excluir com confirmação visual
   */
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isProtectedNode) {
      showToast.warning('Ação bloqueada', 'Nodes de início e fim não podem ser excluídos');
      return;
    }
    onDelete?.();
  };

  /**
   * Inicia teste do fluxo a partir deste node
   */
  const handleTestFromHere = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onTestFromHere?.();
  };

  /**
   * Desagrupa os nodes filhos do grupo
   */
  const handleUngroup = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onUngroup?.();
  };

  return (
    <NodeToolbar
      isVisible={isVisible}
      position={Position.Top}
      offset={8}
      className="flex items-center gap-1 bg-card border border-border rounded-lg shadow-lg p-1 animate-in fade-in-0 zoom-in-95 duration-150"
    >
      <TooltipProvider delayDuration={300}>
        {/* === TOOLBAR PARA NODES DE GRUPO === */}
        {isGroupNode ? (
          <>
            {/* Botão Editar grupo (adicionar/remover nodes) */}
            {canEnterEditMode && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-primary/10 hover:text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onEnterEditMode?.();
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Editar grupo</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Botão Duplicar grupo completo */}
            {canDuplicateGroup && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-emerald-500/10 hover:text-emerald-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onDuplicateGroup?.();
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Duplicar grupo</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Botão Desagrupar */}
            {canUngroup && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-primary/10 hover:text-primary"
                    onClick={handleUngroup}
                  >
                    <Minimize2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Desagrupar nodes</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Botão Copiar ID do grupo */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-blue-500/10 hover:text-blue-600"
                  onClick={handleCopyId}
                >
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="font-mono text-xs">{nodeId}</p>
              </TooltipContent>
            </Tooltip>

            {/* Botão Excluir grupo */}
            {canDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                    onClick={handleDelete}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Excluir grupo (mantém filhos)</p>
                </TooltipContent>
              </Tooltip>
            )}
          </>
        ) : (
          /* === TOOLBAR PADRÃO PARA NODES NORMAIS === */
          <>
            {/* Botão Visualizar/Editar */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-primary/10 hover:text-primary disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onEdit?.();
                  }}
                  disabled={!canEdit}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{canEdit ? 'Visualizar / Editar' : 'Não editável'}</p>
              </TooltipContent>
            </Tooltip>

            {/* Botão Testar a partir deste node (Play) */}
            {canTestFromHere && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-emerald-500/10 hover:text-emerald-600"
                    onClick={handleTestFromHere}
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Testar a partir daqui</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Botão Copiar ID */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-blue-500/10 hover:text-blue-600"
                  onClick={handleCopyId}
                >
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="font-mono text-xs">{nodeId}</p>
              </TooltipContent>
            </Tooltip>

            {/* Botão Duplicar Node */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-emerald-500/10 hover:text-emerald-600 disabled:opacity-50"
                  onClick={handleDuplicate}
                  disabled={!canDuplicate}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{canDuplicate ? 'Duplicar node' : 'Não pode ser duplicado'}</p>
              </TooltipContent>
            </Tooltip>

            {/* Botão Excluir */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                  onClick={handleDelete}
                  disabled={!canDelete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{canDelete ? 'Excluir node' : 'Não pode ser excluído'}</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </TooltipProvider>
    </NodeToolbar>
  );
}
